import autosize from 'autosize'
import { useLayoutEffect, useRef, useState } from 'react'
import { Button } from 'shared/components/Button/Button'
import { TextAreaInput } from 'shared/components/TextInput/TextAreaInput'
import { formFill } from 'shared/theme/colors'
import styled from 'styled-components'

import { isConsole } from '../../config/config'
import { Handler } from '../../helpers/typeHelper'
import { useNoteAvailableTags } from '../../hooks/query/useNoteAvailableTags'
import { NoteRecordType, NoteTagWithLabel } from '../../models/note'
import { FlexBox } from '../Base/FlexBox'
import { NoteComposerAddTags } from './NoteComposerAddTags'

interface NoteComposerProps {
  recordId: string
  recordType: NoteRecordType
  message?: string
  tags?: NoteTagWithLabel[]
  onCancel: Handler
  onMessage: (value: string, tags?: string[]) => Promise<void>
}

export function NoteComposer({
  onCancel,
  onMessage,
  message: initialMessage,
  tags: initialTags,
  recordId,
  recordType,
}: NoteComposerProps) {
  const textarea = useRef<HTMLTextAreaElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState(initialMessage || '')
  const [addedTags, setAddedTags] = useState<string[]>(initialTags?.map((t) => t.value) || [])
  const enableTags = isConsole()
  const { tags: availableTags } = useNoteAvailableTags({ recordId, recordType }, { enabled: enableTags })

  useLayoutEffect(() => {
    if (textarea.current) {
      const el = textarea.current
      autosize(el)

      return () => {
        const evt = new Event('autosize:destroy', { bubbles: true, cancelable: false })
        el?.dispatchEvent(evt)
      }
    }
  }, [])

  function handleTextChange(e: any) {
    setMessage(e.target.value)
  }

  async function handleSubmit() {
    if (!message) return
    setIsLoading(true)
    await onMessage(message, addedTags || undefined)
    setMessage('')
    setAddedTags([])
    setIsLoading(false)
  }

  return (
    <Composer>
      <TextAreaInput
        ref={textarea}
        rows={2}
        noMargin
        placeholder="Type your note"
        value={message}
        onChange={handleTextChange}
      >
        {enableTags && availableTags?.length ? (
          <NoteComposerAddTags
            availableTags={availableTags}
            addedTags={addedTags}
            setAddedTags={setAddedTags}
          />
        ) : null}
      </TextAreaInput>

      <FlexBox alignItems="center" gap="15px" justifyContent="flex-end" mt={2}>
        <Button variant="text" size="medium" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" size="medium" onClick={handleSubmit} isLoading={isLoading}>
          Save
        </Button>
      </FlexBox>
    </Composer>
  )
}

const Composer = styled.div`
  bottom: 0;
  z-index: 5;

  .textarea-wrap {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    border-radius: 3px;
    height: auto;
    flex-flow: column;
    background: ${formFill};
  }

  textarea {
    padding-top: 15px;
    max-height: 400px;
    overflow-x: hidden !important;
  }
`
